import React, { useEffect, useState } from 'react';
import AddEditForm from '../../../sharedComponents/AddEditForm';
import { PencilIcon, TrashIcon, PlusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/solid';
import Modal from 'react-modal';
import DeleteConfirm from '../../../sharedComponents/DeleteConfirm';
import { toast } from 'react-toastify';
import useManageBrands from './useManageBrands';

const customStyles = {
    content: {
      top: '50%',
      left: '50%',
      right: 'auto',
      bottom: 'auto',
      height: 'auto',
      width: '500px',
      marginRight: '-50%',
      transform: 'translate(-50%, -50%)',
    },
  };

const items = [
  {
    title: 'Brand Name',
    name: 'name',
    type: 'text',
    placeholder: 'e.g., Gucci, Nike, Uniqlo'
  },
  {
    title: 'Brand Type',
    name: 'brandType',
    type: 'select',
    options: [
      { value: 'normal', label: 'Normal' },
      { value: 'street_brand', label: 'Street Brand' },
      { value: 'brand_name', label: 'Brand Name' }
    ]
  },
  {
    title: 'Description',
    name: 'description',
    type: 'textarea',
    placeholder: 'Additional notes about the brand'
  },
  {
    title: 'Is Active',
    name: 'isActive',
    type: 'checkbox',
  },
];

const initialValue = {
  id: '',
  name: '',
  brandType: 'normal',
  description: '',
  isActive: true,
}

const BrandSetting = () => {
  const [mode, setMode] = useState('register');
  const [modalTitle, setModalTitle] = useState('Register Brand');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [brand, setBrand] = useState(initialValue);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBrandType, setFilterBrandType] = useState('');

  const {
    brands,
    isLoading,
    error,
    addBrand,
    editBrand,
    deleteBrand,
    refreshBrands,
    searchBrand
  } = useManageBrands();

  const handleInputChange = (e) => {
    const {name, value, type, checked} = e.target;
    setBrand({
      ...brand, 
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const openForm = async (isEdit, id) => {
    if(isEdit){
      const brandToEdit = brands.find(b => b.id === id);
      if(brandToEdit) {
        setBrand(brandToEdit);
        setModalTitle('Update Brand');
        setMode('edit');
      }
    } else {
      setBrand(initialValue);
      setMode('register');
      setModalTitle('Register Brand');
    }
    setIsFormOpen(true);
  };

  const closeForm = () => {
    setIsFormOpen(false);
  };

  const openConfirm = async (id) => {
    const brandToDelete = brands.find(b => b.id === id);
    if(brandToDelete) {
      setBrand(brandToDelete);
      setIsConfirmOpen(true);
    }
  };

  const closeConfirm = () => {
    setIsConfirmOpen(false);
  };

  const handleRegisterBrand = async () => {
    try {
      const returnResult = await addBrand(brand);
      if (returnResult) {
        toast.success('Brand registered successfully!');
        closeForm();
      } else {
        toast.error(error);
      }
    } catch (err) {
      toast.error('An error occurred while registering brand');
    }
  };

  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  const handleUpdateBrand = async () => {
    try {
      await editBrand(brand.id, brand);
      toast.success('Brand updated successfully!');
      closeForm();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    }
  };

  const handleDeleteBrand = async (id) => {
    try {
      await deleteBrand(id);
      toast.success('Brand deleted successfully!');
      closeConfirm();
    } catch (error) {
      const errorMessage = error.response?.data?.message || "An unexpected error occurred.";
      toast.error(errorMessage);
    }
  };

  useEffect(() => {
    const filters = {};
    if (filterBrandType) filters.brandType = filterBrandType;
    if (searchTerm) filters.search = searchTerm;
    refreshBrands(filters);
  }, [searchTerm, filterBrandType]);

  const getBrandTypeBadge = (brandType) => {
    const badges = {
      normal: 'bg-gray-100 text-gray-800',
      street_brand: 'bg-purple-100 text-purple-800',
      brand_name: 'bg-blue-100 text-blue-800'
    };
    return badges[brandType] || badges.normal;
  };

  const getBrandTypeLabel = (brandType) => {
    const labels = {
      normal: 'Normal',
      street_brand: 'Street Brand',
      brand_name: 'Brand Name'
    };
    return labels[brandType] || brandType;
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className='text-5xl'>จัดการแบรนด์</h2>
        <button
          onClick={() => openForm(false)}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded flex items-center gap-2"
        >
          <PlusIcon className="h-5 w-5" />
          เพิ่มแบรนด์ใหม่
        </button>
      </div>

      {/* Filters */}
      <div className="mb-4 flex gap-4 items-end">
        <div className="flex-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">ค้นหาแบรนด์</label>
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="ค้นหาชื่อแบรนด์..."
              className="w-full px-3 py-2 pl-10 border border-gray-300 rounded-md"
            />
            <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">ประเภทแบรนด์</label>
          <select
            value={filterBrandType}
            onChange={(e) => setFilterBrandType(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-md"
          >
            <option value="">ทั้งหมด</option>
            <option value="normal">Normal</option>
            <option value="street_brand">Street Brand</option>
            <option value="brand_name">Brand Name</option>
          </select>
        </div>
      </div>

      {/* Loader */}
      {isLoading && (
        <div className="flex justify-center items-center h-60">
          <div className="loader"></div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="my-4 p-4 bg-red-100 text-red-500 border border-red-400 rounded">
          An error occurred: {error}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border border-gray-300">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 border-b border-gray-300 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ชื่อแบรนด์
              </th>
              <th className="px-6 py-3 border-b border-gray-300 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                ประเภทแบรนด์
              </th>
              <th className="px-6 py-3 border-b border-gray-300 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                คำอธิบาย
              </th>
              <th className="px-6 py-3 border-b border-gray-300 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                สถานะ
              </th>
              <th className="px-6 py-3 border-b border-gray-300 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                การดำเนินการ
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {brands && brands.length > 0 && brands.map((item) => (
              <tr key={item.id} className="hover:bg-gray-50">
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                  {item.name}
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getBrandTypeBadge(item.brandType)}`}>
                    {getBrandTypeLabel(item.brandType)}
                  </span>
                </td>
                <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate">
                  {item.description || '-'}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    item.isActive 
                      ? 'bg-green-100 text-green-800' 
                      : 'bg-red-100 text-red-800'
                  }`}>
                    {item.isActive ? 'ใช้งาน' : 'ไม่ใช้งาน'}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openForm(true, item.id)}
                      className="text-indigo-600 hover:text-indigo-900 p-1 rounded"
                      title="แก้ไข"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => openConfirm(item.id)}
                      className="text-red-600 hover:text-red-900 p-1 rounded"
                      title="ลบ"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {(!brands || brands.length === 0) && (
              <tr>
                <td colSpan="5" className="px-6 py-8 text-center text-gray-500">
                  ไม่มีข้อมูลแบรนด์
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Modal */}
      <Modal
        isOpen={isFormOpen}
        onRequestClose={closeForm}
        style={customStyles}
        ariaHideApp={false}
      >
        <AddEditForm 
          items={items} 
          name={modalTitle} 
          data={brand} 
          mode={mode}
          closeForm={closeForm}
          handleInputChange={handleInputChange}
          handleRegister={handleRegisterBrand}
          handleUpdate={handleUpdateBrand}
          stateValue={brand}
        />
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isConfirmOpen}
        onRequestClose={closeConfirm}
        style={customStyles}
        ariaHideApp={false}
      >
        <DeleteConfirm 
          name="Brand"
          data={brand}
          closeConfirm={closeConfirm}
          handleDelete={handleDeleteBrand}
        />
      </Modal>
    </div>
  );
};

export default BrandSetting;
